import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Phone, Zap, Fuel, Gauge, Lightbulb,
  Usb, Armchair, Package, Disc, Footprints, ShieldCheck, X, Battery, Weight,
} from "lucide-react";
import useSEO from "../hooks/useSEO";

const IMG = (f) => `${process.env.PUBLIC_URL || ""}/santini/${f}`;

const VARIANTEN = [
  {
    icon: Zap,
    tag: "Elektrisch",
    badge: "100% Elektrisch",
    badgeBg: "bg-[#1E9E52]",
    naam: "Santini E-Capri",
    prijs: "vanaf € 2.395",
    prijsnoot: "adviesprijs incl. btw, rijklaar",
    image: "ecapri-zwart.png",
    pitch:
      "Dé elektrische scooter die eruitziet zoals iedereen een scooter kent — maar dan in een modern, elektrisch jasje. Krachtig, stil en schoon, met een uitneembare accu die je overal kunt opladen.",
    specs: [
      ["Motor", "2000W Bosch"],
      ["Accu", "Lithium-Ion Panasonic (1 of 2)"],
      ["Topsnelheid", "25 of 45 km/u"],
      ["Actieradius", "75 – 150 km"],
      ["Laadtijd", "100% in ±6u (80% in ±2u)"],
      ["Gewicht", "86 kg rijklaar"],
    ],
    plus: [
      "Uitneembare accu — laad hem overal op",
      "Lang zadel, geschikt voor twee personen",
      "USB-oplader in het handschoenenvakje",
      "Lage verzekeringspremie & onderhoudskosten",
    ],
  },
  {
    icon: Fuel,
    tag: "Benzine",
    badge: "Benzine",
    badgeBg: "bg-[#C77A1A]",
    naam: "Santini Capri E5",
    prijs: "€ 1.895",
    prijsnoot: "incl. btw",
    image: "capri-benzine.png",
    pitch:
      "Een prachtige verschijning én dankzij het nieuwste injectie-motorblok één van de zuinigste scooters op de markt. Voldoet aan de strenge Euro 5-norm en verbruikt slechts ±1L per 50 km.",
    specs: [
      ["Motor", "50cc EFI 4-takt injectie"],
      ["Emissienorm", "Euro 5"],
      ["Verbruik", "±1L / 50 km"],
      ["Teller", "Digitaal scherm"],
      ["Verlichting", "Full-LED"],
      ["Topsnelheid", "25 of 45 km/u"],
    ],
    plus: [
      "±30% zuiniger dan een carburateurmotor",
      "Digitale teller met alle info in één oogopslag",
      "LED-matrix knipperlichten & LED-koplamp",
      "Ruime buddyruimte — een helm past er zo in",
    ],
  },
];

const FEATURES = [
  {
    image: "f-teller.jpeg",
    t: "Digitale teller",
    d: "Het overzichtelijke digitale scherm toont in één oogopslag snelheid, toerental, tijd, tank- of batterijniveau en meer. Zo ben je onderweg altijd op alles voorbereid.",
  },
  {
    image: "f-koplamp.jpeg",
    t: "Full-LED verlichting",
    d: "Een strakke LED-koplamp met dynamische LED-matrix knipperlichten. Niet alleen mooi, maar ook aanzienlijk meer licht dan een gewone halogeenlamp.",
  },
  {
    image: "f-zadel.jpg",
    t: "Luxe doorgestikt zadel",
    d: "Elk model komt met een comfortabel, doorgestikt zadel met ingegraveerd Santini-logo. Genoeg ruimte voor twee, met opbergruimte onder het zadel voor je helm.",
  },
  {
    image: "f-motor.jpg",
    t: "Euro 5 injectiemotor",
    d: "Het nieuwste type injectie-motorblok maakt de benzine-Capri niet alleen zuinig, maar ook toekomstbestendig dankzij de Euro 5-emissienorm.",
  },
];

const ICON_FEATURES = [
  { icon: Usb, t: "USB-aansluiting", d: "Laad je smartphone onderweg op via de standaard USB-poort." },
  { icon: Footprints, t: "Inklapbare voetsteunen", d: "Solide op het frame gemonteerd — veilig meerijden op één druk." },
  { icon: Package, t: "Ruime buddyruimte", d: "Onder het zadel past met gemak een volledige helm." },
  { icon: Disc, t: "10-spaaks velgen", d: "Sportieve velgen met brede CTS-banden voor comfort en grip." },
  { icon: ShieldCheck, t: "Inklapbare achterdrager", d: "Hoogglans zwarte drager die ook dienstdoet als rugsteun." },
  { icon: Battery, t: "Uitneembare accu", d: "Neem de lichte Panasonic-accu mee en laad hem waar je wilt." },
  { icon: Armchair, t: "Sport handvatten", d: "Comfortabele, stijlvolle handvatten voor extra grip." },
  { icon: Weight, t: "Lichte constructie", d: "Slechts 86 kg rijklaar — wendbaar en makkelijk te hanteren." },
];

const ELEKTRISCH_KLEUREN = [
  { naam: "Mat Antraciet", thumb: "eantra-1.jpg", photos: ["eantra-1.jpg", "eantra-2.jpg", "eantra-3.jpg", "eantra-4.jpg"] },
  { naam: "Mat Brons", thumb: "ebrons-1.jpg", photos: ["ebrons-1.jpg", "ebrons-2.jpg", "ebrons-3.jpg", "ebrons-4.jpg", "ebrons-5.jpg"] },
  { naam: "Mat Blauw", thumb: "eblauw-1.jpg", photos: ["eblauw-1.jpg", "eblauw-2.jpg", "eblauw-3.jpg", "eblauw-4.jpg"] },
  { naam: "Nardo Grijs", thumb: "enardo-1.jpg", photos: ["enardo-1.jpg", "enardo-2.jpg", "enardo-3.jpg", "enardo-4.jpg", "enardo-5.jpg"] },
];

const BENZINE_KLEUREN = [
  { naam: "Mat Antraciet", thumb: "bantra-1.jpg", photos: ["bantra-1.jpg", "bantra-2.jpg", "bantra-3.jpg", "bantra-4.jpg"] },
  { naam: "Mat Brons", thumb: "bbrons-1.jpg", photos: ["bbrons-1.jpg", "bbrons-2.jpg", "bbrons-3.jpg", "bbrons-4.jpg"] },
  { naam: "Mat Blauw", thumb: "bblauw-1.jpg", photos: ["bblauw-1.jpg", "bblauw-2.jpg", "bblauw-3.jpg"] },
  { naam: "Nardo Grijs", thumb: "bnardo-1.jpg", photos: ["bnardo-1.jpg", "bnardo-2.jpg", "bnardo-3.jpg"] },
];

const HERO_SLIDES = [
  { file: "kleur1.png", naam: "Capri — Mat Zwart" },
  { file: "kleur2.png", naam: "Capri — Mat Blauw" },
  { file: "kleur3.png", naam: "Capri — Nardo Grijs" },
  { file: "kleur4.png", naam: "Capri — Mat Brons" },
];

function HeroSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((i) => (i + 1) % HERO_SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="santini-hero-slider"
      className="relative w-full aspect-[4/3] bg-white border border-[#2A2B31] overflow-hidden"
    >
      {HERO_SLIDES.map((s, i) => (
        <img
          key={s.file}
          src={IMG(s.file)}
          alt={`Santini ${s.naam}`}
          loading={i === 0 ? "eager" : "lazy"}
          className="absolute inset-0 w-full h-full object-contain p-6 md:p-10 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: active === i ? 1 : 0 }}
        />
      ))}
      {/* caption */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-5 py-4 flex items-center justify-between">
        <span className="font-mono-tt uppercase text-[11px] tracking-[0.2em] text-white">
          {HERO_SLIDES[active].naam}
        </span>
        <div className="flex items-center gap-1.5">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={s.file}
              type="button"
              aria-label={`Toon ${s.naam}`}
              data-testid={`santini-hero-dot-${i}`}
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                active === i ? "w-6 bg-[#E10600]" : "w-1.5 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Santini() {
  useSEO({
    title: "Santini scooters — Officieel verdeler | TT Motorsports",
    description:
      "TT Motorsports is officieel verdeler van Santini scooters. Ontdek de Santini Capri (E-Capri elektrisch & Capri E5 benzine): alle modellen, kleuren en foto's. Advies, informatie en verkoop.",
    keywords:
      "Santini, Santini scooters, Santini Capri, E-Capri elektrisch, Capri E5 benzine, Santini kleuren, Santini verdeler, scooter kopen Torhout, TT Motorsports",
  });

  const [lightbox, setLightbox] = useState(null); // { naam, photos, drivetrain, photo }

  const openColor = (c, drivetrain) => setLightbox({ naam: c.naam, photos: c.photos, drivetrain, photo: 0 });
  const closeLightbox = () => setLightbox(null);
  const stepPhoto = (dir) =>
    setLightbox((lb) => {
      if (!lb) return lb;
      const total = lb.photos.length;
      return { ...lb, photo: (lb.photo + dir + total) % total };
    });

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") stepPhoto(1);
      if (e.key === "ArrowLeft") stepPhoto(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const tile = (c, idx, variant) => {
    const el = variant === "electric";
    return (
      <motion.button
        key={`${variant}-${c.naam}`}
        type="button"
        onClick={() => openColor(c, variant)}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: (idx % 4) * 0.06 }}
        data-testid={`santini-kleur-${idx}`}
        className="group relative bg-[#0A0A0B] border border-[#2A2B31] hover:border-[#E10600] transition-colors overflow-hidden text-left"
      >
        <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-black/70 text-white text-[10px] font-700 uppercase tracking-wider px-2 py-1">
          <Package size={11} strokeWidth={2.5} /> {c.photos.length} foto{c.photos.length > 1 ? "'s" : ""}
        </span>
        <div className="aspect-[4/3] overflow-hidden bg-black flex items-center justify-center">
          <img
            src={IMG(c.thumb)}
            alt={`Santini ${el ? "E-Capri" : "Capri E5"} ${c.naam}`}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="px-4 py-3 border-t border-[#2A2B31] flex items-center justify-between gap-2">
          <div>
            <span className="font-mono-tt uppercase text-[11px] tracking-[0.15em] text-white block mb-2">{c.naam}</span>
            {el ? (
              <span className="inline-flex items-center gap-1 bg-[#1E9E52]/15 border border-[#1E9E52]/40 text-[#4ADE80] text-[9px] font-700 uppercase tracking-wider px-1.5 py-0.5">
                <Zap size={9} strokeWidth={2.5} /> Elektrisch
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-[#C77A1A]/15 border border-[#C77A1A]/40 text-[#E0A353] text-[9px] font-700 uppercase tracking-wider px-1.5 py-0.5">
                <Fuel size={9} strokeWidth={2.5} /> Benzine
              </span>
            )}
          </div>
          <span className="text-[#5A5C63] group-hover:text-[#E10600] transition-colors shrink-0">
            <ArrowRight size={18} />
          </span>
        </div>
      </motion.button>
    );
  };

  return (
    <div data-testid="santini-page" className="bg-[#0A0A0B]">
      {/* HEADER */}
      <section className="relative pt-36 pb-16 md:pt-44 md:pb-20 overflow-hidden grain border-b border-[#2A2B31]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0F] to-[#0A0A0B]" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link
              to="/"
              data-testid="santini-back"
              className="inline-flex items-center gap-2 text-[#9A9CA3] hover:text-[#E10600] font-display uppercase text-sm font-600 mb-8 transition-colors"
            >
              <ArrowLeft size={16} /> Terug naar home
            </Link>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 border border-[#E10600] px-3 py-1.5 mb-6">
                <BadgeCheck size={14} className="text-[#E10600]" />
                <span className="font-mono-tt uppercase text-[11px] tracking-[0.25em] text-[#E10600]">
                  Officieel Santini-verdeler
                </span>
              </div>
              <h1 className="heading-xl text-[clamp(3rem,10vw,7rem)] text-white mb-6 leading-none">SANTINI</h1>
              <p className="text-lg md:text-xl text-[#B8B9BE] leading-relaxed max-w-xl">
                <span className="text-white">Kwaliteit, modern en luxe.</span> Daar staat Santini voor. Bij TT
                Motorsports vind je het volledige Santini-gamma — <span className="text-white">elektrisch én
                benzine</span> — met deskundig advies, alle info en verkoop op één plek.
              </p>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative"
          >
            <HeroSlider />
          </motion.div>
        </div>
      </section>

      {/* MODELLEN / VARIANTEN */}
      <section className="section-pad border-b border-[#2A2B31]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="mb-12">
            <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Het gamma</span>
            <h2 className="heading-xl text-4xl md:text-6xl text-white mt-3">De Santini Capri</h2>
            <p className="text-[#9A9CA3] mt-4 max-w-2xl">
              Beschikbaar in twee uitvoeringen. Kies voor de stille kracht van elektrisch of de vertrouwde
              zuinigheid van benzine — beide met dezelfde luxe afwerking.
            </p>
          </div>
          <div className="mb-10 flex flex-col sm:flex-row gap-3">
            <div className="flex items-start gap-3 flex-1 bg-[#0F1A12] border border-[#1E9E52]/40 px-5 py-4">
              <Zap size={20} className="text-[#1E9E52] shrink-0 mt-0.5" strokeWidth={2.2} />
              <p className="text-sm text-[#B8B9BE] leading-relaxed">
                <span className="text-[#4ADE80] font-600">Santini E-Capri = 100% elektrisch.</span> Stil, schoon en
                zonder brandstof. Uitneembare accu.
              </p>
            </div>
            <div className="flex items-start gap-3 flex-1 bg-[#1A140C] border border-[#C77A1A]/40 px-5 py-4">
              <Fuel size={20} className="text-[#C77A1A] shrink-0 mt-0.5" strokeWidth={2.2} />
              <p className="text-sm text-[#B8B9BE] leading-relaxed">
                <span className="text-[#E0A353] font-600">Santini Capri E5 = benzine.</span> Zuinige Euro 5
                injectiemotor. Zelfde design & kleuren als de E-Capri.
              </p>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {VARIANTEN.map((v, i) => (
              <motion.div
                key={v.naam}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`santini-variant-${i}`}
                className="bg-[#141518] border border-[#2A2B31] hover:border-[#E10600]/50 transition-colors overflow-hidden flex flex-col"
              >
                <div className="relative bg-white p-6">
                  <img src={IMG(v.image)} alt={v.naam} className="w-full h-56 object-contain" loading="lazy" />
                  <div className={`absolute top-4 left-4 inline-flex items-center gap-2 ${v.badgeBg} px-3.5 py-2 shadow-lg`}>
                    <v.icon size={16} className="text-white" strokeWidth={2.2} />
                    <span className="font-display uppercase text-xs font-700 tracking-[0.12em] text-white">
                      {v.badge}
                    </span>
                  </div>
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-end justify-between gap-4 mb-4">
                    <h3 className="font-display uppercase font-700 text-2xl md:text-3xl text-white leading-none">
                      {v.naam}
                    </h3>
                    <div className="text-right shrink-0">
                      <div className="text-[#E10600] font-display font-700 text-lg leading-none">{v.prijs}</div>
                      <div className="text-[#5A5C63] text-[11px] mt-1">{v.prijsnoot}</div>
                    </div>
                  </div>
                  <p className="text-[#B8B9BE] leading-relaxed mb-6">{v.pitch}</p>
                  <div className="grid grid-cols-2 gap-px bg-[#2A2B31] border border-[#2A2B31] mb-6">
                    {v.specs.map(([k, val]) => (
                      <div key={k} className="bg-[#0A0A0B] px-4 py-3">
                        <div className="font-mono-tt text-[10px] uppercase tracking-wider text-[#5A5C63]">{k}</div>
                        <div className="text-white text-sm font-600">{val}</div>
                      </div>
                    ))}
                  </div>
                  <ul className="space-y-2 mb-7">
                    {v.plus.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-[#9A9CA3] text-sm">
                        <BadgeCheck size={16} className="text-[#E10600] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="mt-auto inline-flex items-center justify-center gap-2 bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 text-sm px-6 py-3.5 transition-colors"
                  >
                    Prijs & beschikbaarheid <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KLEUREN GALERIJ */}
      <section className="section-pad border-b border-[#2A2B31]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="mb-12">
            <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Prachtige kleuren</span>
            <h2 className="heading-xl text-4xl md:text-6xl text-white mt-3">Kies jouw kleur</h2>
            <p className="text-[#9A9CA3] mt-4 max-w-2xl">
              De elektrische <span className="text-[#4ADE80]">E-Capri</span> en de benzine{" "}
              <span className="text-[#E0A353]">Capri E5</span> hebben elk hun eigen kleuren. Klik op een kleur
              om alle foto's in het groot te bekijken.
            </p>
          </div>

          {/* ELEKTRISCH */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#1E9E52] flex items-center justify-center">
                <Zap size={20} className="text-white" strokeWidth={2.2} />
              </div>
              <h3 className="font-display uppercase font-700 text-2xl md:text-3xl text-white leading-none">
                E-Capri <span className="text-[#4ADE80]">— Elektrisch</span>
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {ELEKTRISCH_KLEUREN.map((c, i) => tile(c, i, "electric"))}
            </div>
          </div>

          {/* BENZINE */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C77A1A] flex items-center justify-center">
                <Fuel size={20} className="text-white" strokeWidth={2.2} />
              </div>
              <h3 className="font-display uppercase font-700 text-2xl md:text-3xl text-white leading-none">
                Capri E5 <span className="text-[#E0A353]">— Benzine</span>
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {BENZINE_KLEUREN.map((c, i) => tile(c, i + 4, "benzine"))}
            </div>
          </div>
        </div>
      </section>

      {/* KENMERKEN met foto's — Uitrusting (onderaan) */}
      <section className="section-pad border-b border-[#2A2B31]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="mb-12">
            <span className="font-mono-tt uppercase text-xs tracking-[0.3em] text-[#E10600]">Uitrusting</span>
            <h2 className="heading-xl text-4xl md:text-6xl text-white mt-3">Tot in de details afgewerkt</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
                data-testid={`santini-feature-${i}`}
                className="group bg-[#141518] border border-[#2A2B31] overflow-hidden"
              >
                <div className="overflow-hidden">
                  <img
                    src={IMG(f.image)}
                    alt={f.t}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display uppercase font-700 text-xl text-white mb-2">{f.t}</h3>
                  <p className="text-[#9A9CA3] text-sm leading-relaxed">{f.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2B31] border border-[#2A2B31]">
            {ICON_FEATURES.map((k) => (
              <div key={k.t} className="bg-[#0D0D0F] p-7 hover:bg-[#141518] transition-colors">
                <k.icon size={26} className="text-[#E10600] mb-4" strokeWidth={1.5} />
                <h3 className="font-display uppercase font-700 text-white mb-2 text-sm">{k.t}</h3>
                <p className="text-[#9A9CA3] text-sm leading-relaxed">{k.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#E10600]" />
        <div className="absolute inset-0 racing-stripe opacity-10" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="heading-xl text-3xl md:text-5xl text-white max-w-2xl">
              Klaar om jouw Santini te ontdekken?
            </h2>
            <p className="text-white/80 mt-3">Kom langs voor advies of vraag vrijblijvend meer informatie.</p>
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            <a
              href="tel:0485741524"
              className="bg-[#0A0A0B] hover:bg-black text-white font-display uppercase font-700 tracking-wide px-8 py-4 flex items-center gap-2 transition-colors"
            >
              <Phone size={18} /> 0485 74 15 24
            </a>
            <Link
              to="/contact"
              data-testid="santini-cta-contact"
              className="border border-white/40 hover:bg-white/10 text-white font-display uppercase font-700 tracking-wide px-8 py-4 flex items-center gap-2 transition-colors"
            >
              Contacteer ons <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <div
          data-testid="santini-lightbox"
          onClick={closeLightbox}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 md:p-8"
        >
          <button
            data-testid="santini-lightbox-close"
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors z-20"
          >
            <X size={32} />
          </button>

          {lightbox.photos.length > 1 && (
            <>
              <button
                data-testid="santini-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); stepPhoto(-1); }}
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-[#E10600] text-white p-3 transition-colors"
                aria-label="Vorige foto"
              >
                <ArrowLeft size={24} />
              </button>
              <button
                data-testid="santini-lightbox-next"
                onClick={(e) => { e.stopPropagation(); stepPhoto(1); }}
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-white/10 hover:bg-[#E10600] text-white p-3 transition-colors"
                aria-label="Volgende foto"
              >
                <ArrowRight size={24} />
              </button>
            </>
          )}

          <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-4 w-full max-w-3xl">
            <div className="relative w-full p-4 bg-black">
              <span
                className={`absolute top-6 left-6 z-10 inline-flex items-center gap-1.5 text-white font-display uppercase text-[11px] font-700 tracking-wider px-2.5 py-1.5 ${
                  lightbox.drivetrain === "electric" ? "bg-[#1E9E52]" : "bg-[#C77A1A]"
                }`}
              >
                {lightbox.drivetrain === "electric" ? <Zap size={12} strokeWidth={2.5} /> : <Fuel size={12} strokeWidth={2.5} />}
                {lightbox.drivetrain === "electric" ? "Elektrisch" : "Benzine"}
              </span>
              <img
                key={lightbox.photo}
                src={IMG(lightbox.photos[lightbox.photo])}
                alt={`Santini ${lightbox.drivetrain === "electric" ? "E-Capri" : "Capri E5"} ${lightbox.naam} foto ${lightbox.photo + 1}`}
                className="w-full object-contain max-h-[70vh]"
              />
            </div>
            {/* thumbnails */}
            {lightbox.photos.length > 1 && (
              <div className="flex items-center gap-2">
                {lightbox.photos.map((p, pi) => (
                  <button
                    key={p}
                    data-testid={`santini-lightbox-thumb-${pi}`}
                    onClick={() => setLightbox((lb) => ({ ...lb, photo: pi }))}
                    className={`w-16 h-16 border-2 transition-colors bg-black ${lightbox.photo === pi ? "border-[#E10600]" : "border-transparent hover:border-white/50"}`}
                  >
                    <img src={IMG(p)} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="absolute bottom-6 left-0 right-0 text-center">
            <span className="font-mono-tt uppercase tracking-[0.2em] text-white text-sm">
              {lightbox.naam}
            </span>
            <span className="ml-3 text-white/50 text-sm">
              {lightbox.photo + 1} / {lightbox.photos.length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
